import CloseIcon from '@mui/icons-material/Close';
import { Box, Divider, Drawer, IconButton, ListItemButton, ListItemIcon, ListItemText, Stack } from '@mui/material';
import { grey } from '@mui/material/colors';
import { GenerateReportIcon, HomeIcon, ReportHistoryIcon, SupportIcon } from 'components/Icons';
import { primaryColors } from 'config/defaultTheme';
import { ROUTES_DEFINITIONS } from 'config/routes/routeDefinitions';
import { useTranslation } from 'react-i18next';


export interface AdminUserInfo {
  firstName: string;
  lastName: string;
  email: string;
}

export interface NavMenuDrawerProps {
  adminUser: AdminUserInfo;
  isOpen: boolean;
  onClose: () => void;
  isBatchReportsEnabled?: boolean;
}

type MenuItem = {
  key: string;
  label: string;
  Icon: React.ElementType;
  path?: any;
  external?: boolean;
  hidden?: boolean;
};

const NavMenu = ({
  adminUser,
  isOpen,
  onClose,
  isBatchReportsEnabled = false,
}: NavMenuDrawerProps) => {
  const { t } = useTranslation('admin');

  const menuGroups: MenuItem[][] = [
    [
      {
        key: 'home',
        label: t('navMenu.home'),
        Icon: HomeIcon,
        path: ROUTES_DEFINITIONS.reports.path,
      },
      {
        key: 'generate',
        label: t('navMenu.generateReports'),
        Icon: GenerateReportIcon,
        path: ROUTES_DEFINITIONS.reports.path,
        hidden: true,
      },
    ],
    [
      {
        key: 'history',
        label: t('navMenu.reportsHistory'),
        Icon: ReportHistoryIcon, path: ROUTES_DEFINITIONS.reportHistory.path,
        hidden: !isBatchReportsEnabled,
      },
      {
        key: 'support',
        label: t('navMenu.support'),
        Icon: SupportIcon,
        path: 'https://support.wiley.com/s/article/LPI-New-Leader-Report-Experience-FAQ',
        external: true,
      },
    ],
  ];

  return (
    <Drawer
      data-testid="NavMenuDrawer-test-id"
      anchor="left"
      open={isOpen}
      onClose={onClose}
      sx={{ position: 'relative' }}
    >
      <Box sx={{
        p: 2,
        height: 1,
        position: 'relative',
        backgroundColor: 'white',
        minWidth: '259px',
      }}>
        <IconButton
          sx={{ position: 'absolute', top: '1rem', right: '1rem', p: 0 }}
          onClick={onClose}
          data-testid="NavMenuCloseButton-test-id"
        >
          <CloseIcon
            sx={{
              width: '24px',
              height: 'auto',
            }}
          />
        </IconButton>

{ adminUser && adminUser?.firstName && adminUser?.lastName && (
  <>
        <Stack
          direction="row"
          gap={2}
          mt={2}
        >

          <Stack sx={{
            borderRadius: '100%',
            backgroundColor: primaryColors.midnight,
            width: '31px',
            height: '31px',
            color: 'white',
            fontFamily: 'Open Sans, sans-serif',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1rem',
            fontWeight: 700,
            lineHeight: '1.25rem',
          }}>{adminUser?.firstName?.charAt(0)}{adminUser?.lastName?.charAt?.(0)}</Stack>

          <Box
            sx={{
              color: grey[800],
              fontWeight: 400,
            }}
          >
            <Box sx={{ fontSize: '0.875rem' }}>
              { `${adminUser?.firstName} ${adminUser?.lastName}` }
            </Box>

            <Box sx={{ fontSize: '0.625rem', lineHeight: '1rem' }}>
              { adminUser?.email }
            </Box>
          </Box>
        </Stack>

        <Divider sx={{ my: 3, borderColor: grey[300] }} />
        </>
)}


        <Box sx={{ position: 'relative', mb: 2, mt: 4 }}>
          {menuGroups.map((group, gi) => (
            <Box key={gi}>
              {(() => {
                const visibleItems = group.filter(item => !item.hidden);
                if (visibleItems.length === 0) return null;

                return (
                <>
                {visibleItems.map(item => {
                  const Icon = item.Icon;
                  return (
                  <ListItemButton
                    data-testid={`NavMenuItem-${item.key}-test-id`}
                    key={item.key}
                    {...(item.path ? {
                      component: 'a',
                      href: item.path,
                      target: item.external ? '_blank' : undefined,
                      rel: item.external ? 'noopener noreferrer' : undefined,
                    } : {})}
                  >
                    <ListItemIcon sx={{ width: '20px', height: '20px', minWidth: '36px' }}>
                    <Icon sx={{ color: 'primary.main', width: '20px', height: '20px' }} />
                    </ListItemIcon>

                    <ListItemText primary={item.label} />
                  </ListItemButton>
                  );
                })}

                {gi < menuGroups.length - 1 && (
                  <Divider sx={{ my: 3, borderColor: grey[300] }} />
                )}
                </>
                );
              })()}
            </Box>
          ))}
        </Box>
      </Box>
    </Drawer>
  );
};

export default NavMenu;
